import { createFileRoute } from "@tanstack/react-router";
import { BlueprintButton, BlueprintGrid, BpFrame } from "@/components/blueprint/Blueprint";
import { AppScreenshotToggle } from "@/components/shared/AppCapturePlan";
import { SEOHead } from "@/components/shared/SEOHead";
import { APP_STORE_URL } from "@/lib/utils";

export const Route = createFileRoute("/editor")({
  component: EditorPage,
});

const editorFeatures = [
  {
    label: "Layers",
    title: "Layered Composition",
    body: "Stack artwork, text, masks, effects, and product-aware elements without flattening the creative process too early.",
  },
  {
    label: "Clean",
    title: "Background Removal",
    body: "Cut out subjects from photos, isolate the good part of a moment, and keep the design ready for product placement.",
  },
  {
    label: "Shape",
    title: "Transform Controls",
    body: "Crop, rotate, scale, position, and refine designs so a quick capture can become a clean piece of merch artwork.",
  },
  {
    label: "Style",
    title: "Effects & Variations",
    body: "Explore visual directions, compare versions, and keep improving as new editor tools are added to the studio.",
  },
];

function EditorPage() {
  return (
    <div className="editor-page page-enter">
      <SEOHead
        title="Quuil Image Editor"
        description="Explore the Quuil image editor: layers, background removal, transformations, effects, variations, and product-aware artwork tools."
        path="/editor"
        keywords={["image editor", "photo to merch", "layer editor", "background removal", "custom merch editor"]}
      />

      <section className="editor-hero" aria-labelledby="editor-heading">
        <BlueprintGrid />
        <div className="editor-container editor-hero-grid">
          <div className="editor-copy">
            <span className="bp-kicker">Image Editor // Always Expanding</span>
            <h1 id="editor-heading">Clean Up, Layer, And Refine</h1>
            <p>
              QUUIL's editor is built for the messy middle after a photo, prompt, sketch, or Inkwell design becomes an idea worth keeping. Remove backgrounds, build with layers, transform the artwork, compare variations, and keep shaping it for real products.
            </p>
            <div className="bp-hero-actions justify-start">
              <BlueprintButton href={APP_STORE_URL}>Get The App</BlueprintButton>
              <BlueprintButton href="/showcase" variant="ghost">Back To Showcase</BlueprintButton>
            </div>
          </div>

          <div className="editor-phone-stage" aria-label="Quuil image editor preview">
            <AppScreenshotToggle
              title="Image Editor"
              lightSrc="/clean-screens/imageeditordetailsdark.png"
              darkSrc="/clean-screens/imageeditordetailsdark.png"
              alt="Quuil image editor screen"
              priority="Advanced Toolset"
              imageClassName="editor-phone-placeholder"
              placeholderClassName="editor-phone-placeholder"
              className="editor-phone-placeholder-toggle"
              showModeToggle={false}
            />
          </div>
        </div>
      </section>

      <section className="editor-section" aria-labelledby="editor-features-heading">
        <BlueprintGrid light />
        <div className="editor-container">
          <div className="bp-section-title">
            <span className="bp-kicker">Built For Real Artwork</span>
            <h2 id="editor-features-heading">More Than A Quick Filter</h2>
            <p>
              The editor is where a captured moment becomes product-ready. It is advanced today, and designed to keep growing as the QUUIL studio adds new creative controls.
            </p>
          </div>

          <div className="editor-feature-grid">
            {editorFeatures.map((feature) => (
              <BpFrame className="editor-feature-card" label={feature.label} key={feature.title}>
                <h3>{feature.title}</h3>
                <p>{feature.body}</p>
              </BpFrame>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
